import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Tablet, ExternalLink, RefreshCw, Wifi, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * v1.2 — Fleet-wide kiosks list.
 * Reuses GET /api/kiosks (no site_id → returns all kiosks the role can see)
 * and joins client-side against GET /api/sites for site names. Per-kiosk
 * actions (Pair / Repair / Unpair) deep-link to the existing per-site kiosk
 * pairing flow on /sites/{id}.
 */
export default function Kiosks() {
  const [kiosks, setKiosks] = useState([]);
  const [sitesById, setSitesById] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: k }, { data: s }] = await Promise.all([
        api.get("/kiosks"),
        api.get("/sites"),
      ]);
      setKiosks(k || []);
      setSitesById(Object.fromEntries((s || []).map((x) => [x.id, x])));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const heartbeatLabel = (iso) => {
    if (!iso) return "Never";
    const delta = (Date.now() - new Date(iso).getTime()) / 1000;
    if (delta < 90) return `${Math.round(delta)}s ago`;
    if (delta < 3600) return `${Math.round(delta / 60)} min ago`;
    if (delta < 86400) return `${Math.round(delta / 3600)} h ago`;
    return `${Math.round(delta / 86400)} d ago`;
  };

  return (
    <AppShell>
      <div data-testid="kiosks-page">
        <div className="flex items-end justify-between gap-3 mb-6">
          <div>
            <div className="eb-overline mb-1">Fleet</div>
            <h1 className="text-3xl font-semibold text-navy-900 inline-flex items-center gap-2">
              <Tablet className="w-6 h-6 text-orange-500" /> Kiosks
            </h1>
            <p className="text-sm text-navy-500 mt-1">
              Every kiosk across every site you can see. Pair, repair, and unpair from the
              site detail page.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="kiosks-refresh">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        {loading ? (
          <div className="bg-white border border-navy-100 rounded-xl p-10 text-center text-sm text-navy-500">
            Loading…
          </div>
        ) : kiosks.length === 0 ? (
          <div className="bg-white border border-navy-100 rounded-xl p-10 text-center">
            <Tablet className="w-8 h-8 text-navy-300 mx-auto mb-2" />
            <div className="text-sm font-medium text-navy-900">No kiosks yet</div>
            <p className="text-sm text-navy-500 mt-1">Pair your first kiosk from any site.</p>
          </div>
        ) : (
          <div className="bg-white border border-navy-100 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-navy-50 text-navy-500 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Site</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Last heartbeat</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {kiosks.map((k) => {
                  const site = sitesById[k.site_id];
                  const online = k.last_heartbeat_at && (Date.now() - new Date(k.last_heartbeat_at).getTime() < 120_000);
                  return (
                    <tr key={k.id} className="border-t border-navy-100" data-testid={`kiosk-row-${k.id}`}>
                      <td className="px-4 py-3 font-medium text-navy-900">{k.name || k.id}</td>
                      <td className="px-4 py-3 text-navy-700">{site?.name || k.site_id || "—"}</td>
                      <td className="px-4 py-3">
                        {k.paired_at ? (
                          <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${online ? "text-success-700" : "text-warning-700"}`}>
                            {online ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
                            {online ? "Online" : "Paired (offline)"}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-navy-500">
                            Pending pair
                            {k.pairing_pin && (
                              <code className="ml-1 font-mono bg-navy-50 px-1.5 rounded">
                                PIN {k.pairing_pin}
                              </code>
                            )}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-navy-500">{heartbeatLabel(k.last_heartbeat_at)}</td>
                      <td className="px-4 py-3 text-right">
                        {k.site_id ? (
                          <Link
                            to={`/sites/${k.site_id}`}
                            className="inline-flex items-center gap-1 text-orange-700 hover:underline text-sm"
                            data-testid={`kiosk-row-link-${k.id}`}
                          >
                            Manage <ExternalLink className="w-3.5 h-3.5" />
                          </Link>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppShell>
  );
}
